export interface PdfFallbackSourceItem {
  pageNum?: number;
  itemIndex?: number;
  text?: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  fontName?: string;
  fontSize?: number;
  confidence?: number;
  [key: string]: unknown;
}

export interface PdfFallbackTextItem {
  pageNum: number;
  itemIndex: number;
  text: string;
  x: number;
  y: number;
  width: number;
  height: number;
  fontName?: string;
  fontSize?: number;
  confidence?: number;
}

export type PdfFallbackPageItemsByPage = Record<
  string,
  PdfFallbackSourceItem[]
>;

export interface PdfFallbackBoundary {
  pageNumber: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PdfFallbackDimensionItem {
  pageNumber: number;
  itemIndex: number;
  text: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PdfFallbackSearchResult {
  boundary: PdfFallbackBoundary | null;
  dimensionItems: PdfFallbackDimensionItem[];
  matchedItems: PdfFallbackTextItem[];
  pageNumber: number | null;
  pageNumbers: number[];
}

interface NormalizedCharMapEntry {
  pageNumber: number;
  itemIndex: number;
  sourceItem: PdfFallbackTextItem;
}

interface NormalizedSearchScope {
  scopeId: string;
  normalizedText: string;
  charMap: NormalizedCharMapEntry[];
  items: PdfFallbackTextItem[];
}

interface AnchoredScopeFragmentMatch {
  item: PdfFallbackTextItem;
  score: number;
  matchedText?: string;
  normalizedNeedle: string;
  normalizedAnchorText: string;
}

interface AnchoredScopeMatch {
  match: AnchoredScopeFragmentMatch;
  start: number;
  end: number;
  text: string;
}

interface AnchoredScopeMatchState {
  anchoredMatches: AnchoredScopeMatch[];
  coveredChars: number;
  totalMatchScore: number;
  firstStart: number | null;
  lastEnd: number;
}

interface AnchoredScopeMatchResult {
  matches: AnchoredScopeFragmentMatch[];
  combinedText: string;
  normalizedCombinedText: string;
  normalizedTargetText: string;
  score: number;
  exact: boolean;
}

export interface PdfFallbackSearchOptions {
  caseSensitive?: boolean;
  allowLooseWhitespace?: boolean;
  pageNumber?: number;
  pageNumbers?: number[];
}

const MIN_ANCHORED_SCOPE_MATCH_SCORE = 0.72;
const ANCHORED_SCOPE_FRAGMENT_MATCH_THRESHOLD = 0.82;

/**
 * Finds all PDF text items that match a search string across one or more PDF text items.
 *
 * Designed for PDF highlighting where text may be split across lines/items and contain:
 * - weird control characters
 * - smart quotes
 * - extra spacing
 * - line wrapping
 * - split text runs
 */
export function findPdfFallbackMatches(
  searchValue: string,
  pageItems: PdfFallbackSourceItem[] | PdfFallbackPageItemsByPage,
  options: PdfFallbackSearchOptions = {}
): PdfFallbackSearchResult[] {
  const {
    caseSensitive = false,
    allowLooseWhitespace = true,
    pageNumber,
    pageNumbers
  } = options;
  const requestedPageNumbers = resolveRequestedPageNumbers({
    pageNumber,
    pageNumbers
  });

  const normalizedSearch = normalizeSearchText(searchValue, {
    caseSensitive,
    collapseWhitespace: allowLooseWhitespace
  });

  if (!normalizedSearch) {
    return [];
  }

  const normalizedPageItems = normalizePdfFallbackSearchInput(pageItems, {
    pageNumber,
    pageNumbers
  });
  const scopes = buildNormalizedSearchScopes(normalizedPageItems, {
    caseSensitive,
    collapseWhitespace: allowLooseWhitespace,
    requestedPageNumbers
  });
  const dedupedResults = new Map<string, PdfFallbackSearchResult>();

  for (const scope of scopes) {
    const matchRanges = findAllStringRanges(
      scope.normalizedText,
      normalizedSearch
    );
    let hasContiguousMatch = false;

    for (const range of matchRanges) {
      hasContiguousMatch = true;
      const matchedMapEntries = scope.charMap.slice(range.start, range.end);
      const matchedItemMap = new Map<string, PdfFallbackTextItem>();

      for (const entry of matchedMapEntries) {
        matchedItemMap.set(getPdfFallbackItemKey(entry.sourceItem), entry.sourceItem);
      }

      appendMatchedItemsResult(
        dedupedResults,
        Array.from(matchedItemMap.values()).sort(sortPdfItemsByDocumentOrder)
      );
    }

    if (hasContiguousMatch) {
      continue;
    }

    const anchoredScopeMatchItems = findAnchoredScopeMatchItems(
      scope.items,
      searchValue,
      {
        caseSensitive,
        collapseWhitespace: allowLooseWhitespace
      }
    );

    appendMatchedItemsResult(dedupedResults, anchoredScopeMatchItems);
  }

  return Array.from(dedupedResults.values()).sort((left, right) => {
    const leftPageNumber = left.pageNumbers[0] ?? 0;
    const rightPageNumber = right.pageNumbers[0] ?? 0;

    if (leftPageNumber !== rightPageNumber) {
      return leftPageNumber - rightPageNumber;
    }

    const leftItemIndex = left.matchedItems[0]?.itemIndex ?? 0;
    const rightItemIndex = right.matchedItems[0]?.itemIndex ?? 0;
    return leftItemIndex - rightItemIndex;
  });
}

export function countPdfFallbackSearchInputItems(
  pageItems: PdfFallbackSourceItem[] | PdfFallbackPageItemsByPage,
  options: Pick<PdfFallbackSearchOptions, 'pageNumber' | 'pageNumbers'> = {}
): number {
  return normalizePdfFallbackSearchInput(pageItems, options).length;
}

function getPdfFallbackItemKey(item: PdfFallbackTextItem): string {
  return `${item.pageNum}:${item.itemIndex}`;
}

function createDimensionItems(
  matchedItems: PdfFallbackTextItem[]
): PdfFallbackDimensionItem[] {
  return matchedItems.map((item) => ({
    pageNumber: item.pageNum,
    itemIndex: item.itemIndex,
    text: item.text,
    x: item.x,
    y: item.y,
    width: item.width,
    height: item.height
  }));
}

function buildSearchResultFromMatchedItems(
  matchedItems: PdfFallbackTextItem[]
): PdfFallbackSearchResult | null {
  if (matchedItems.length === 0) {
    return null;
  }

  const normalizedMatchedItems = dedupePdfFallbackItems(matchedItems);
  const pageNumbers = normalizePageNumbers(
    normalizedMatchedItems.map((item) => item.pageNum)
  );

  return {
    boundary:
      pageNumbers.length === 1
        ? buildBoundary(pageNumbers[0], normalizedMatchedItems)
        : null,
    dimensionItems: createDimensionItems(normalizedMatchedItems),
    matchedItems: normalizedMatchedItems,
    pageNumber: pageNumbers[0] ?? null,
    pageNumbers
  };
}

function appendMatchedItemsResult(
  dedupedResults: Map<string, PdfFallbackSearchResult>,
  matchedItems: PdfFallbackTextItem[]
) {
  const result = buildSearchResultFromMatchedItems(matchedItems);

  if (!result) {
    return;
  }

  const resultKey = result.matchedItems
    .map((item) => getPdfFallbackItemKey(item))
    .join('|');

  if (!resultKey || dedupedResults.has(resultKey)) {
    return;
  }

  dedupedResults.set(resultKey, result);
}

function normalizePageNumbers(pageNumbers: Array<number | null | undefined>) {
  return Array.from(
    new Set(
      pageNumbers.filter(
        (pageNumber): pageNumber is number =>
          typeof pageNumber === 'number' &&
          Number.isInteger(pageNumber) &&
          pageNumber > 0
      )
    )
  ).sort((leftPageNumber, rightPageNumber) => leftPageNumber - rightPageNumber);
}

function resolveRequestedPageNumbers(
  options: Pick<PdfFallbackSearchOptions, 'pageNumber' | 'pageNumbers'>
) {
  return normalizePageNumbers([
    ...(options.pageNumbers ?? []),
    options.pageNumber
  ]);
}

function expandRequestedPageNumbers(
  requestedPageNumbers: number[]
): number[] {
  return normalizePageNumbers(
    requestedPageNumbers.flatMap((pageNumber) => [pageNumber, pageNumber + 1])
  );
}

function toPositivePageNumber(value: unknown): number | null {
  return typeof value === 'number' &&
    Number.isInteger(value) &&
    value > 0
    ? value
    : null;
}

function normalizePdfFallbackSearchInput(
  pageItems: PdfFallbackSourceItem[] | PdfFallbackPageItemsByPage,
  options: Pick<PdfFallbackSearchOptions, 'pageNumber' | 'pageNumbers'>
): PdfFallbackTextItem[] {
  const explicitPageNumbers = resolveRequestedPageNumbers(options);
  const searchScopePageNumbers = expandRequestedPageNumbers(explicitPageNumbers);
  const allowedPageNumbers =
    searchScopePageNumbers.length > 0
      ? new Set(searchScopePageNumbers)
      : null;

  if (Array.isArray(pageItems)) {
    return pageItems.flatMap((item, itemIndex) => {
      const normalizedItem = normalizePdfFallbackSourceItem(item, itemIndex);

      if (!normalizedItem) {
        return [];
      }

      if (
        allowedPageNumbers !== null &&
        !allowedPageNumbers.has(normalizedItem.pageNum)
      ) {
        return [];
      }

      return [normalizedItem];
    });
  }

  const pageEntries =
    searchScopePageNumbers.length > 0
      ? searchScopePageNumbers.map(
          (resolvedPageNumber) =>
            [
              String(resolvedPageNumber),
              pageItems[String(resolvedPageNumber)] ?? []
            ] as const
        )
      : Object.entries(pageItems).sort(([leftPageKey], [rightPageKey]) => {
          if (leftPageKey === 'unknown') {
            return 1;
          }

          if (rightPageKey === 'unknown') {
            return -1;
          }

          return Number(leftPageKey) - Number(rightPageKey);
        });

  return pageEntries.flatMap(([pageKey, pageItemsForPage]) => {
    if (!Array.isArray(pageItemsForPage)) {
      return [];
    }

    const fallbackPageNumber = toPositivePageNumber(Number(pageKey));

    return pageItemsForPage.flatMap((item, itemIndex) => {
      const normalizedItem = normalizePdfFallbackSourceItem(
        item,
        itemIndex,
        fallbackPageNumber
      );

      return normalizedItem ? [normalizedItem] : [];
    });
  });
}

function normalizePdfFallbackSourceItem(
  item: PdfFallbackSourceItem,
  fallbackItemIndex: number,
  fallbackPageNumber?: number | null
): PdfFallbackTextItem | null {
  const pageNum =
    toPositivePageNumber(item.pageNum) ?? fallbackPageNumber ?? null;
  const text = typeof item.text === 'string' ? item.text : '';

  if (pageNum === null || !text.trim()) {
    return null;
  }

  return {
    confidence: typeof item.confidence === 'number' ? item.confidence : undefined,
    fontName: typeof item.fontName === 'string' ? item.fontName : undefined,
    fontSize: typeof item.fontSize === 'number' ? item.fontSize : undefined,
    height: typeof item.height === 'number' ? item.height : 0,
    itemIndex:
      typeof item.itemIndex === 'number' ? item.itemIndex : fallbackItemIndex,
    pageNum,
    text,
    width: typeof item.width === 'number' ? item.width : 0,
    x: typeof item.x === 'number' ? item.x : 0,
    y: typeof item.y === 'number' ? item.y : 0
  };
}

function buildNormalizedSearchScopes(
  pageItems: PdfFallbackTextItem[],
  options: {
    caseSensitive: boolean;
    collapseWhitespace: boolean;
    requestedPageNumbers: number[];
  }
): NormalizedSearchScope[] {
  const pageScopes = buildNormalizedPageScopes(pageItems, options);
  const adjacentPageScopes = buildNormalizedAdjacentPageScopes(
    pageItems,
    options
  );
  const clauseScopes = buildNormalizedClauseScopes(pageItems, options);

  return [...pageScopes, ...adjacentPageScopes, ...clauseScopes];
}

function buildNormalizedPageScopes(
  pageItems: PdfFallbackTextItem[],
  options: {
    caseSensitive: boolean;
    collapseWhitespace: boolean;
    requestedPageNumbers: number[];
  }
): NormalizedSearchScope[] {
  const itemsByPage = new Map<number, PdfFallbackTextItem[]>();
  const requestedPageNumbers =
    options.requestedPageNumbers.length > 0
      ? new Set(options.requestedPageNumbers)
      : null;

  for (const item of pageItems) {
    if (!itemsByPage.has(item.pageNum)) {
      itemsByPage.set(item.pageNum, []);
    }

    itemsByPage.get(item.pageNum)!.push(item);
  }

  const pageScopes: NormalizedSearchScope[] = [];

  for (const [pageNumber, items] of itemsByPage.entries()) {
    if (
      requestedPageNumbers !== null &&
      !requestedPageNumbers.has(pageNumber)
    ) {
      continue;
    }

    const scope = buildNormalizedScopeFromItems(
      [...items].sort(sortPdfItemsForReadingOrder),
      options,
      `page:${pageNumber}`
    );

    if (scope) {
      pageScopes.push(scope);
    }
  }

  return pageScopes.sort((leftScope, rightScope) =>
    leftScope.scopeId.localeCompare(rightScope.scopeId)
  );
}

function buildNormalizedAdjacentPageScopes(
  pageItems: PdfFallbackTextItem[],
  options: {
    caseSensitive: boolean;
    collapseWhitespace: boolean;
    requestedPageNumbers: number[];
  }
): NormalizedSearchScope[] {
  const itemsByPage = new Map<number, PdfFallbackTextItem[]>();

  for (const item of pageItems) {
    if (!itemsByPage.has(item.pageNum)) {
      itemsByPage.set(item.pageNum, []);
    }

    itemsByPage.get(item.pageNum)!.push(item);
  }

  const startPageNumbers =
    options.requestedPageNumbers.length > 0
      ? options.requestedPageNumbers
      : Array.from(itemsByPage.keys()).sort(
          (leftPageNumber, rightPageNumber) =>
            leftPageNumber - rightPageNumber
        );
  const adjacentPageScopes: NormalizedSearchScope[] = [];

  for (const startPageNumber of startPageNumbers) {
    const currentPageItems = itemsByPage.get(startPageNumber) ?? [];
    const nextPageItems = itemsByPage.get(startPageNumber + 1) ?? [];

    if (currentPageItems.length === 0 || nextPageItems.length === 0) {
      continue;
    }

    const scope = buildNormalizedScopeFromItems(
      [...currentPageItems, ...nextPageItems].sort(
        sortPdfItemsByDocumentOrder
      ),
      options,
      `page-span:${startPageNumber}-${startPageNumber + 1}`
    );

    if (scope) {
      adjacentPageScopes.push(scope);
    }
  }

  return adjacentPageScopes;
}

function buildNormalizedClauseScopes(
  pageItems: PdfFallbackTextItem[],
  options: {
    caseSensitive: boolean;
    collapseWhitespace: boolean;
    requestedPageNumbers: number[];
  }
): NormalizedSearchScope[] {
  const orderedItems = [...pageItems].sort(sortPdfItemsByDocumentOrder);
  const requestedPageNumbers =
    options.requestedPageNumbers.length > 0
      ? new Set(options.requestedPageNumbers)
      : null;
  const numberedItems = orderedItems
    .map((item, index) => ({
      depth: getClauseNumberDepth(item.text),
      index,
      item
    }))
    .filter(
      (
        value
      ): value is {
        depth: number;
        index: number;
        item: PdfFallbackTextItem;
      } => value.depth !== null && value.depth >= 2
    );
  const clauseScopes: NormalizedSearchScope[] = [];

  for (let numberedIndex = 0; numberedIndex < numberedItems.length; numberedIndex += 1) {
    const currentNumberedItem = numberedItems[numberedIndex];

    if (
      requestedPageNumbers !== null &&
      !requestedPageNumbers.has(currentNumberedItem.item.pageNum)
    ) {
      continue;
    }

    const nextNumberedItemIndex =
      numberedItems[numberedIndex + 1]?.index ?? orderedItems.length;
    const bodyItems = orderedItems
      .slice(currentNumberedItem.index + 1, nextNumberedItemIndex)
      .filter((item) => getClauseNumberDepth(item.text) === null);

    if (bodyItems.length === 0) {
      continue;
    }

    const parentBoundaryIndex = findNearestParentClauseBoundaryIndex(
      numberedItems,
      numberedIndex
    );
    const preambleItems = resolvePreambleItems(
      orderedItems,
      parentBoundaryIndex + 1,
      currentNumberedItem.index
    );
    const scopeItems = dedupePdfFallbackItems([
      ...preambleItems,
      ...bodyItems
    ]);
    const scope = buildNormalizedScopeFromItems(
      scopeItems,
      options,
      `clause:${currentNumberedItem.item.pageNum}:${currentNumberedItem.item.itemIndex}`
    );

    if (scope) {
      clauseScopes.push(scope);
    }
  }

  return clauseScopes;
}

function findNearestParentClauseBoundaryIndex(
  numberedItems: Array<{
    depth: number;
    index: number;
    item: PdfFallbackTextItem;
  }>,
  currentNumberedIndex: number
): number {
  const currentDepth = numberedItems[currentNumberedIndex]?.depth ?? 0;

  for (
    let numberedIndex = currentNumberedIndex - 1;
    numberedIndex >= 0;
    numberedIndex -= 1
  ) {
    if (numberedItems[numberedIndex].depth < currentDepth) {
      return numberedItems[numberedIndex].index;
    }
  }

  return -1;
}

function resolvePreambleItems(
  orderedItems: PdfFallbackTextItem[],
  startIndex: number,
  endIndexExclusive: number
): PdfFallbackTextItem[] {
  let preambleEndIndex = -1;

  for (let itemIndex = endIndexExclusive - 1; itemIndex >= startIndex; itemIndex -= 1) {
    if (isPreambleAnchorText(orderedItems[itemIndex].text)) {
      preambleEndIndex = itemIndex;
      break;
    }
  }

  if (preambleEndIndex === -1) {
    return [];
  }

  return orderedItems
    .slice(startIndex, preambleEndIndex + 1)
    .filter((item) => getClauseNumberDepth(item.text) === null);
}

function dedupePdfFallbackItems(
  items: PdfFallbackTextItem[]
): PdfFallbackTextItem[] {
  const seenItems = new Set<string>();
  const dedupedItems: PdfFallbackTextItem[] = [];

  for (const item of items) {
    const itemKey = getPdfFallbackItemKey(item);

    if (seenItems.has(itemKey)) {
      continue;
    }

    seenItems.add(itemKey);
    dedupedItems.push(item);
  }

  return dedupedItems.sort(sortPdfItemsByDocumentOrder);
}

function buildNormalizedScopeFromItems(
  items: PdfFallbackTextItem[],
  options: {
    caseSensitive: boolean;
    collapseWhitespace: boolean;
  },
  scopeId: string
): NormalizedSearchScope | null {
  let normalizedText = '';
  const charMap: NormalizedCharMapEntry[] = [];

  for (const item of items) {
    const normalizedItemText = normalizeSearchText(item.text, {
      caseSensitive: options.caseSensitive,
      collapseWhitespace: options.collapseWhitespace
    });

    if (!normalizedItemText) {
      continue;
    }

    if (normalizedText.length > 0) {
      normalizedText += ' ';
      charMap.push({
        itemIndex: item.itemIndex,
        pageNumber: item.pageNum,
        sourceItem: item
      });
    }

    for (const char of normalizedItemText) {
      normalizedText += char;
      charMap.push({
        itemIndex: item.itemIndex,
        pageNumber: item.pageNum,
        sourceItem: item
      });
    }
  }

  if (!normalizedText || charMap.length === 0) {
    return null;
  }

  return {
    charMap,
    items,
    normalizedText,
    scopeId
  };
}

function isPreambleAnchorText(text: string): boolean {
  return text.trim().endsWith(':');
}

function getClauseNumberDepth(text: string): number | null {
  const trimmedText = text.trim();

  if (!/^\d+(?:\.\d+)*\.?$/.test(trimmedText)) {
    return null;
  }

  const normalizedText = trimmedText.endsWith('.')
    ? trimmedText.slice(0, -1)
    : trimmedText;

  if (!normalizedText) {
    return 1;
  }

  return normalizedText.split('.').filter(Boolean).length;
}

function sortPdfItemsByDocumentOrder(
  leftItem: PdfFallbackTextItem,
  rightItem: PdfFallbackTextItem
): number {
  if (leftItem.pageNum !== rightItem.pageNum) {
    return leftItem.pageNum - rightItem.pageNum;
  }

  if (leftItem.itemIndex !== rightItem.itemIndex) {
    return leftItem.itemIndex - rightItem.itemIndex;
  }

  return sortPdfItemsForReadingOrder(leftItem, rightItem);
}

function normalizeSearchText(
  value: string,
  options: {
    caseSensitive: boolean;
    collapseWhitespace: boolean;
  }
): string {
  let text = value ?? '';

  text = text
    .normalize('NFKC')

    // Remove control characters, including the weird char in your example: \u0003
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, ' ')

    // Normalize smart quotes / curly quotes
    .replace(/[“”„‟]/g, '"')
    .replace(/[‘’‚‛]/g, "'")

    // Normalize dashes
    .replace(/[‐-‒–—―]/g, '-')

    // Normalize non-breaking spaces and other odd spaces
    .replace(/[\u00A0\u1680\u180E\u2000-\u200B\u202F\u205F\u3000]/g, ' ')

    // Ignore punctuation differences between query text and PDF text runs
    .replace(/[.,;:!?()[\]{}]/g, ' ');

  if (options.collapseWhitespace) {
    text = text.replace(/\s+/g, ' ').trim();
  }

  if (!options.caseSensitive) {
    text = text.toLowerCase();
  }

  /**
   * Optional cleanup:
   * Your search string does not include quotes around Access Date,
   * but the PDF item has `"Access Date"`.
   *
   * This makes:
   *   `"Access Date" means`
   * match:
   *   `Access Date means`
   */
  text = text.replace(/"([^"]+)"/g, '$1');

  return text;
}

const LOOSE_SEARCH_NON_ALNUM_REGEX = (() => {
  try {
    return new RegExp('[^\\p{L}\\p{N}]+', 'gu');
  } catch {
    return null;
  }
})();

function isLikelyLooseSearchTextChar(char: string): boolean {
  if (/^[A-Za-z0-9]$/.test(char)) {
    return true;
  }

  if (!char.trim()) {
    return false;
  }

  const codePoint = char.codePointAt(0);

  if (codePoint === undefined) {
    return false;
  }

  // Exclude common Unicode punctuation/symbol blocks in the fallback path.
  if (
    (codePoint >= 0x2000 && codePoint <= 0x206f) ||
    (codePoint >= 0x20a0 && codePoint <= 0x20cf) ||
    (codePoint >= 0x2190 && codePoint <= 0x27bf) ||
    (codePoint >= 0x2e00 && codePoint <= 0x2e7f) ||
    (codePoint >= 0x3000 && codePoint <= 0x303f)
  ) {
    return false;
  }

  const uppercaseValue = char.toLocaleUpperCase();
  const lowercaseValue = char.toLocaleLowerCase();

  if (uppercaseValue !== lowercaseValue) {
    return true;
  }

  // Treat remaining non-whitespace code points as text characters so
  // scripts without case mappings still survive the loose normalization.
  return true;
}

function normalizeLooseSearchTextFallback(text: string): string {
  return Array.from(text)
    .map((char) => (isLikelyLooseSearchTextChar(char) ? char : ' '))
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeLooseSearchText(
  value: string,
  options: {
    caseSensitive: boolean;
    collapseWhitespace: boolean;
  }
): string {
  const normalizedSearchText = normalizeSearchText(value, options);

  if (LOOSE_SEARCH_NON_ALNUM_REGEX) {
    return normalizedSearchText
      .replace(LOOSE_SEARCH_NON_ALNUM_REGEX, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  return normalizeLooseSearchTextFallback(normalizedSearchText);
}

function diceCoefficient(leftValue: string, rightValue: string): number {
  if (leftValue === rightValue) {
    return 1;
  }

  if (leftValue.length < 2 || rightValue.length < 2) {
    return 0;
  }

  const bigrams = new Map<string, number>();

  for (let index = 0; index < leftValue.length - 1; index += 1) {
    const bigram = leftValue.slice(index, index + 2);
    bigrams.set(bigram, (bigrams.get(bigram) ?? 0) + 1);
  }

  let intersection = 0;

  for (let index = 0; index < rightValue.length - 1; index += 1) {
    const bigram = rightValue.slice(index, index + 2);
    const count = bigrams.get(bigram) ?? 0;

    if (count <= 0) {
      continue;
    }

    bigrams.set(bigram, count - 1);
    intersection += 1;
  }

  return (2 * intersection) / (leftValue.length + rightValue.length - 2);
}

function getSearchWindows(text: string, targetLength: number): string[] {
  const words = text.split(' ').filter(Boolean);
  const resolvedTargetLength = Math.max(1, targetLength);
  const windows: string[] = [];

  for (let index = 0; index < words.length; index += 1) {
    const window = words.slice(index, index + resolvedTargetLength).join(' ');

    if (window) {
      windows.push(window);
    }
  }

  return windows;
}

function buildOrderedSharedAnchorText(
  sourceText: string,
  matchedText: string
): string {
  const sourceWords = sourceText.split(' ').filter(Boolean);
  const matchedWords = matchedText.split(' ').filter(Boolean);
  const sharedWords: string[] = [];
  let matchedWordIndex = 0;

  for (const sourceWord of sourceWords) {
    while (
      matchedWordIndex < matchedWords.length &&
      matchedWords[matchedWordIndex] !== sourceWord
    ) {
      matchedWordIndex += 1;
    }

    if (matchedWordIndex >= matchedWords.length) {
      continue;
    }

    sharedWords.push(sourceWord);
    matchedWordIndex += 1;
  }

  return sharedWords.join(' ');
}

function joinTextFragments(parts: string[]): string {
  let combinedText = '';

  for (const part of parts) {
    const trimmedPart = part.trim();

    if (!trimmedPart) {
      continue;
    }

    if (!combinedText) {
      combinedText = trimmedPart;
      continue;
    }

    if (
      /^[,.;:!?%)\]}]/.test(trimmedPart) ||
      /[-/({]$/.test(combinedText) ||
      combinedText.endsWith('[')
    ) {
      combinedText += trimmedPart;
      continue;
    }

    combinedText += ` ${trimmedPart}`;
  }

  return combinedText;
}

function findAllOccurrences(text: string, fragment: string): number[] {
  if (!fragment) {
    return [];
  }

  const indexes: number[] = [];
  let fromIndex = 0;

  while (fromIndex < text.length) {
    const foundIndex = text.indexOf(fragment, fromIndex);

    if (foundIndex === -1) {
      break;
    }

    indexes.push(foundIndex);
    fromIndex = foundIndex + 1;
  }

  return indexes;
}

function findScopeFragmentMatches(
  items: PdfFallbackTextItem[],
  searchValue: string,
  options: {
    caseSensitive: boolean;
    collapseWhitespace: boolean;
    minLength?: number;
    threshold?: number;
  }
): AnchoredScopeFragmentMatch[] {
  const {
    caseSensitive,
    collapseWhitespace,
    minLength = 3,
    threshold = 1
  } = options;
  const normalizedSearchStrict = normalizeSearchText(searchValue, {
    caseSensitive,
    collapseWhitespace
  });
  const normalizedSearchLoose = normalizeLooseSearchText(searchValue, {
    caseSensitive,
    collapseWhitespace
  });
  const results: AnchoredScopeFragmentMatch[] = [];

  for (const item of items) {
    const rawNeedle = item.text;

    if (!rawNeedle || rawNeedle.trim().length < minLength) {
      continue;
    }

    const needleStrict = normalizeSearchText(rawNeedle, {
      caseSensitive,
      collapseWhitespace
    });
    const needleLoose = normalizeLooseSearchText(rawNeedle, {
      caseSensitive,
      collapseWhitespace
    });

    if (!needleLoose || needleLoose.length < minLength) {
      continue;
    }

    if (normalizedSearchStrict.includes(needleStrict)) {
      results.push({
        item,
        matchedText: rawNeedle,
        normalizedAnchorText: needleStrict,
        normalizedNeedle: needleStrict,
        score: 1
      });
      continue;
    }

    if (normalizedSearchLoose.includes(needleLoose)) {
      results.push({
        item,
        matchedText: rawNeedle,
        normalizedAnchorText: normalizeSearchText(rawNeedle, {
          caseSensitive,
          collapseWhitespace
        }),
        normalizedNeedle: needleLoose,
        score: 0.96
      });
      continue;
    }

    const needleWordCount = needleLoose.split(' ').filter(Boolean).length;
    const candidateWindowSizes = [
      Math.max(1, needleWordCount - 2),
      Math.max(1, needleWordCount - 1),
      needleWordCount,
      needleWordCount + 1,
      needleWordCount + 2
    ];
    let bestScore = 0;
    let bestWindow = '';

    for (const windowSize of candidateWindowSizes) {
      const windows = getSearchWindows(normalizedSearchLoose, windowSize);

      for (const window of windows) {
        const score = diceCoefficient(needleLoose, window);

        if (score <= bestScore) {
          continue;
        }

        bestScore = score;
        bestWindow = window;
      }
    }

    if (bestScore < threshold) {
      continue;
    }

    const sharedAnchorText = buildOrderedSharedAnchorText(
      needleLoose,
      bestWindow
    );

    results.push({
      item,
      matchedText: bestWindow,
      normalizedAnchorText: sharedAnchorText || bestWindow,
      normalizedNeedle: needleLoose,
      score: bestScore
    });
  }

  return results.sort((leftMatch, rightMatch) => rightMatch.score - leftMatch.score);
}

function buildAnchoredScopeMatchResult(
  state: AnchoredScopeMatchState | null,
  normalizedTargetText: string
): AnchoredScopeMatchResult | null {
  if (!state || state.anchoredMatches.length === 0 || state.firstStart === null) {
    return null;
  }

  const exact =
    state.firstStart === 0 && state.lastEnd === normalizedTargetText.length;
  const averageMatchScore =
    state.totalMatchScore / state.anchoredMatches.length;
  const spanCoverage =
    normalizedTargetText.length === 0
      ? 0
      : (state.lastEnd - state.firstStart) / normalizedTargetText.length;
  const anchoredCoverage =
    normalizedTargetText.length === 0
      ? 0
      : state.coveredChars / normalizedTargetText.length;

  return {
    combinedText: exact
      ? normalizedTargetText
      : joinTextFragments(
          state.anchoredMatches.map((anchoredMatch) => anchoredMatch.text)
        ),
    exact,
    matches: state.anchoredMatches.map(
      (anchoredMatch) => anchoredMatch.match
    ),
    normalizedCombinedText: exact
      ? normalizedTargetText
      : normalizedTargetText.slice(state.firstStart, state.lastEnd),
    normalizedTargetText,
    score: exact
      ? 1
      : Math.min(
          0.99,
          (spanCoverage * 0.7 + anchoredCoverage * 0.3) * averageMatchScore
        )
  };
}

function isBetterAnchoredScopeMatchState(
  candidateState: AnchoredScopeMatchState | null,
  currentState: AnchoredScopeMatchState | null,
  normalizedTargetText: string
): boolean {
  const candidateResult = buildAnchoredScopeMatchResult(
    candidateState,
    normalizedTargetText
  );
  const currentResult = buildAnchoredScopeMatchResult(
    currentState,
    normalizedTargetText
  );

  if (!candidateResult) {
    return false;
  }

  if (!currentResult) {
    return true;
  }

  if (candidateResult.exact !== currentResult.exact) {
    return candidateResult.exact;
  }

  const candidateStartsAtBeginning = candidateState?.firstStart === 0;
  const currentStartsAtBeginning = currentState?.firstStart === 0;

  if (candidateStartsAtBeginning !== currentStartsAtBeginning) {
    return candidateStartsAtBeginning;
  }

  const candidateEndsAtTargetEnd =
    candidateState?.lastEnd === normalizedTargetText.length;
  const currentEndsAtTargetEnd =
    currentState?.lastEnd === normalizedTargetText.length;

  if (candidateEndsAtTargetEnd !== currentEndsAtTargetEnd) {
    return candidateEndsAtTargetEnd;
  }

  const candidateSpan =
    candidateState && candidateState.firstStart !== null
      ? candidateState.lastEnd - candidateState.firstStart
      : 0;
  const currentSpan =
    currentState && currentState.firstStart !== null
      ? currentState.lastEnd - currentState.firstStart
      : 0;

  if (candidateSpan !== currentSpan) {
    return candidateSpan > currentSpan;
  }

  if ((candidateState?.coveredChars ?? 0) !== (currentState?.coveredChars ?? 0)) {
    return (candidateState?.coveredChars ?? 0) > (currentState?.coveredChars ?? 0);
  }

  if (candidateResult.score !== currentResult.score) {
    return candidateResult.score > currentResult.score;
  }

  return candidateResult.matches.length > currentResult.matches.length;
}

function findBestAnchoredScopeMatchCombination(
  matches: AnchoredScopeFragmentMatch[],
  searchValue: string,
  options: {
    caseSensitive: boolean;
    collapseWhitespace: boolean;
  }
): AnchoredScopeMatchResult | null {
  const normalizedTargetText = normalizeSearchText(searchValue, {
    caseSensitive: options.caseSensitive,
    collapseWhitespace: options.collapseWhitespace
  });

  if (!normalizedTargetText) {
    return null;
  }

  const orderedMatches = [...matches].sort((leftMatch, rightMatch) =>
    sortPdfItemsByDocumentOrder(leftMatch.item, rightMatch.item)
  );
  const occurrenceCache = new Map<string, number[]>();

  const getOccurrences = (fragment: string) => {
    const cachedValue = occurrenceCache.get(fragment);

    if (cachedValue) {
      return cachedValue;
    }

    const nextOccurrences = findAllOccurrences(normalizedTargetText, fragment);
    occurrenceCache.set(fragment, nextOccurrences);
    return nextOccurrences;
  };

  const memo = new Map<string, AnchoredScopeMatchState | null>();

  const search = (
    startIndex: number,
    minTargetIndex: number
  ): AnchoredScopeMatchState | null => {
    const memoKey = `${startIndex}:${minTargetIndex}`;
    const cachedState = memo.get(memoKey);

    if (cachedState !== undefined) {
      return cachedState;
    }

    let bestState: AnchoredScopeMatchState | null = null;

    for (let index = startIndex; index < orderedMatches.length; index += 1) {
      const fragment = orderedMatches[index].normalizedAnchorText;

      if (!fragment) {
        continue;
      }

      const occurrences = getOccurrences(fragment).filter(
        (occurrence) => occurrence >= minTargetIndex
      );

      for (const occurrenceStart of occurrences) {
        const occurrenceEnd = occurrenceStart + fragment.length;
        const suffixState = search(index + 1, occurrenceEnd);
        const anchoredMatch: AnchoredScopeMatch = {
          end: occurrenceEnd,
          match: orderedMatches[index],
          start: occurrenceStart,
          text: orderedMatches[index].item.text
        };
        const candidateState: AnchoredScopeMatchState = {
          anchoredMatches: [
            anchoredMatch,
            ...(suffixState?.anchoredMatches ?? [])
          ],
          coveredChars: fragment.length + (suffixState?.coveredChars ?? 0),
          firstStart: occurrenceStart,
          lastEnd: suffixState?.lastEnd ?? occurrenceEnd,
          totalMatchScore:
            orderedMatches[index].score + (suffixState?.totalMatchScore ?? 0)
        };

        if (
          isBetterAnchoredScopeMatchState(
            candidateState,
            bestState,
            normalizedTargetText
          )
        ) {
          bestState = candidateState;
        }
      }
    }

    memo.set(memoKey, bestState);
    return bestState;
  };

  return buildAnchoredScopeMatchResult(search(0, 0), normalizedTargetText);
}

function findAnchoredScopeMatchItems(
  items: PdfFallbackTextItem[],
  searchValue: string,
  options: {
    caseSensitive: boolean;
    collapseWhitespace: boolean;
  }
): PdfFallbackTextItem[] {
  if (!searchValue.trim() || items.length === 0) {
    return [];
  }

  const fragmentMatches = findScopeFragmentMatches(items, searchValue, {
    caseSensitive: options.caseSensitive,
    collapseWhitespace: options.collapseWhitespace,
    threshold: ANCHORED_SCOPE_FRAGMENT_MATCH_THRESHOLD
  });

  if (fragmentMatches.length === 0) {
    return [];
  }

  const bestMatchCombination = findBestAnchoredScopeMatchCombination(
    fragmentMatches,
    searchValue,
    options
  );

  if (
    !bestMatchCombination ||
    bestMatchCombination.score < MIN_ANCHORED_SCOPE_MATCH_SCORE
  ) {
    return [];
  }

  const matchedItems = dedupePdfFallbackItems(
    bestMatchCombination.matches.map((match) => match.item)
  );

  if (matchedItems.length < 2 && !bestMatchCombination.exact) {
    return [];
  }

  return matchedItems;
}

function findAllStringRanges(
  fullText: string,
  searchText: string
): Array<{ start: number; end: number }> {
  const ranges: Array<{ start: number; end: number }> = [];

  let fromIndex = 0;

  while (fromIndex < fullText.length) {
    const start = fullText.indexOf(searchText, fromIndex);

    if (start === -1) {
      break;
    }

    const end = start + searchText.length;

    ranges.push({ start, end });

    fromIndex = end;
  }

  return ranges;
}

function sortPdfItemsForReadingOrder(
  a: PdfFallbackTextItem,
  b: PdfFallbackTextItem
): number {
  const yTolerance = Math.max(a.height, b.height, 2) * 0.6;

  const sameLine = Math.abs(a.y - b.y) <= yTolerance;

  if (sameLine) {
    return a.x - b.x;
  }

  return a.y - b.y;
}

function buildBoundary(
  pageNumber: number,
  items: PdfFallbackTextItem[]
): PdfFallbackBoundary | null {
  if (items.length === 0) {
    return null;
  }

  const minX = Math.min(...items.map((item) => item.x));
  const minY = Math.min(...items.map((item) => item.y));
  const maxX = Math.max(...items.map((item) => item.x + item.width));
  const maxY = Math.max(...items.map((item) => item.y + item.height));

  return {
    pageNumber,
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY
  };
}
